/** What happens when a hero visits a map object.
 *
 * The dispatch is on `MapObjectState.kind` - the closed union in `state.ts` -
 * and every number comes out of the object's payload, which is
 * `MapObjectDef.payload` overlaid with whatever the instance has changed. There
 * is no branch on an object id anywhere in this file, and none in the movement
 * code at all: `visitObject` is the single door.
 *
 * ## Payload vocabulary
 *
 * | kind      | keys                                                              |
 * |-----------|-------------------------------------------------------------------|
 * | resource  | `resource`, `amount` (or `min`/`max` to roll)                      |
 * | mine      | `resource`, `amount` (per day)                                     |
 * | dwelling  | `creature`, `growth`, `stock`                                      |
 * | artifact  | `artifact`                                                         |
 * | monster   | `creature`, `count`, `joinCostPerHead`, `flees`                    |
 * | treasure  | `gold`, `experience`                                               |
 * | shrine    | `spell`, `level`                                                   |
 * | scholar   | `primary`, `skill`, `spell`, `amount`                              |
 * | bonus     | `morale`,`luck`,`movement`,`mana`,`manaFull`,`experience`,`gold`,  |
 * |           | `attack`,`defense`,`spellPower`,`knowledge`,`days`,`obelisk`,      |
 * |           | `skill`,`consumed`, plus `service` - see below                     |
 * | boat      | -                                                                  |
 * | garrison  | `antiMagic`                                                        |
 * | portal    | `channel`, `entryOnly`, `exitOnly`, `lossBp`                       |
 * | bank      | `creature`,`count`,`gold`,`artifact`,`rewardCreature`,             |
 * |           | `rewardCount`,`resource`,`amount`,`spell`,`consumed`,`looted`      |
 * | keymaster | `colour`                                                           |
 * | gate      | `colour`, `quest`                                                  |
 * | sign      | `text`                                                             |
 *
 * ## Services
 *
 * A `bonus` whose payload names a `service` is a counter a hero does business
 * at rather than a statue they walk past: it asks a question, takes payment and
 * hands something over. Those live in `services.ts` and `quests.ts`, one branch
 * each, and their payload keys are documented there. `bonus()` offers the
 * service first and falls through to the ordinary stat grants when there is
 * none, which is what lets one def do both.
 */

import type { ArmyId, HeroId, ObjectId, PlayerId } from "../ids.js";
import {
  asArtifactDefId, asCreatureDefId, asModifierSrcId, asSkillDefId, asSpellDefId,
} from "./ids.js";
import type { Reason } from "../commands.js";
import type { MapObjectState, PrimarySkill, ResourceKind, TownState } from "../state.js";
import { RESOURCES } from "../state.js";
import type { Modifier } from "../modifiers.js";
import { entityKeys } from "../util/iter.js";
import { indexOf, setOccupant,
} from "../map/create.js";
import type { Ctx } from "./ctx.js";
import {
  OK_CANCEL, addResource, mintArmyId, optionResponses, patchHero, patchObject,
  putArmy, raise, rollRange,
} from "./ctx.js";
import type { Payload, VisitOutcome } from "./payload.js";
import {
  DONE, STOP, markPlayerVisit, payFlag, payNum, payStr, payloadOf,
  playerMarkKey, removeObject, setPayload,
} from "./payload.js";
import { diplomacyOffer } from "./diplomacy.js";
import { questGateOpen } from "./quests.js";
import { grantReward } from "./reward.js";
import { rollBlackMarket, visitService } from "./services.js";
import { revealStep } from "./visibility.js";
import {
  addCreatures, armyValue, emptyArmySlots, giveArtifact, grantExperience,
  SPELLBOOK_ARTIFACT, SPELLBOOK_SLOT, hasFreeSlotFor, hasSpellbook, learnIllegality, learnSkill, learnSpell,
  maxMana, maxSpellLevel, skillLevel, MAX_SECONDARY_SKILLS,
} from "./hero.js";

// ---------------------------- payload, outcomes, visit marks

/**
 * The vocabulary lives in `payload.ts` so that `services.ts` and `quests.ts`
 * can own a branch of the dispatch without importing this file back. It is
 * re-exported here because this module has always been its door.
 */
export type { Payload, VisitOutcome } from "./payload.js";
export {
  DONE, heroVisited, markPlayerVisit, payFlag, payList, payNum, payPair, payStr,
  payloadOf, playerMarkKey, playerVisited, removeObject, setPayload, STOP,
} from "./payload.js";
/** The prompt-answering half of the services, for `execute.ts`'s resume arm. */
export { applyTimedEvents, resumeService, tradingPosts } from "./services.js";

function markHeroVisit(c: Ctx, id: ObjectId, h: HeroId, oneVisitPerHero: boolean): void {
  const o = c.s.objects[id];
  if (o === undefined) return;
  if (!o.visitedBy.includes(h)) {
    patchObject(c, id, { visitedBy: [...o.visitedBy, h].sort() });
  }
  if (!oneVisitPerHero) return;
  const hero = c.s.heroes[h];
  if (hero !== undefined && !hero.visited.includes(id)) {
    patchHero(c, h, { visited: [...hero.visited, id].sort() });
  }
}

// ------------------------------------------------------------------- keys

/**
 * Border-gate keys are per player and have nowhere of their own to live in
 * `PlayerState`, so a key is "this player has a visit mark on some Keymaster's
 * Tent of that colour". No extra state, and it survives losing the hero.
 */
export function hasKey(c: Ctx, p: PlayerId, colour: string): boolean {
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind !== "keymaster" || o.removed) continue;
    const pay = payloadOf(c, o);
    if (payStr(pay, "colour") !== colour) continue;
    if (pay[playerMarkKey(p)] === true) return true;
  }
  return false;
}

// ------------------------------------------------------------- entry gate

/**
 * May the hero step onto this cell at all?
 *
 * Only three things say no outright: an obstacle, a locked border gate and a
 * Quest Guard whose quest is unfinished. A monster is handled by *entering* -
 * the fight prompt is raised from the visit, which is what makes "attack the
 * guard" and "walk onto the chest" one code path.
 *
 * The two gate families are one arm because they are one rule with two ledgers:
 * a border gate reads the Keymaster's Tents, a Quest Guard reads the Seer's
 * Huts, and both ledgers are per player and live in the objects' own payloads.
 */
export function entryIllegality(c: Ctx, heroId: HeroId, o: MapObjectState): Reason | null {
  const h = c.s.heroes[heroId];
  if (h === undefined) return { code: "NO_SUCH_ENTITY", id: heroId as string };
  if (o.kind === "obstacle") return { code: "PATH_BLOCKED", atIndex: 0 };
  if (o.kind === "gate") {
    const pay = payloadOf(c, o);
    const colour = payStr(pay, "colour");
    const quest = payStr(pay, "quest");
    if (colour === "quest" || quest !== "") {
      if (!questGateOpen(c, h.owner, quest)) return { code: "TARGET_INVALID" };
      return null;
    }
    if (colour !== "" && !hasKey(c, h.owner, colour)) return { code: "TARGET_INVALID" };
  }
  return null;
}

// ------------------------------------------------------------------ visit

export interface VisitOptions {
  /** The rest of the hero's path, carried into any prompt this visit raises. */
  readonly path?: readonly number[];
  /** Set when the hero walked onto the object rather than clicking it. */
  readonly moved?: boolean;
}

export function visitObject(
  c: Ctx, heroId: HeroId, objectId: ObjectId, opts: VisitOptions = {},
): VisitOutcome {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined) return { kind: "blocked", reason: { code: "NO_SUCH_ENTITY", id: heroId } };
  if (o === undefined || o.removed) return DONE;

  const def = c.content.mapObjects.get(o.def);
  const oneVisit = def?.oneVisitPerHero ?? false;
  if (oneVisit && h.visited.includes(objectId)) return DONE;

  const pay = payloadOf(c, o);
  const path = opts.path ?? [];
  const out = dispatch(c, heroId, o, pay, path);
  if (out.kind === "done" || out.kind === "stop") {
    c.events.push({ type: "ObjectVisited", hero: heroId, object: objectId, def: o.def });
    markHeroVisit(c, objectId, heroId, oneVisit);
    markPlayerVisit(c, objectId, h.owner);
  }
  return out;
}

function dispatch(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  switch (o.kind) {
    case "resource": return takeResource(c, heroId, o, pay);
    case "mine": return capture(c, heroId, o);
    case "dwelling": return dwelling(c, heroId, o, pay);
    case "artifact": return pickUpArtifact(c, heroId, o, pay);
    case "monster": return monster(c, heroId, o, pay, path);
    case "bank": return bank(c, heroId, o, pay, path);
    case "treasure": return treasure(c, heroId, o, pay, path);
    case "shrine": return shrine(c, heroId, pay);
    case "scholar": return scholar(c, heroId, o, pay);
    case "bonus": return bonus(c, heroId, o, pay, path);
    case "boat": return boat(c, heroId, o);
    case "garrison": return garrison(c, heroId, o, pay, path);
    case "portal": return portal(c, heroId, o, pay, path);
    case "keymaster": {
      markPlayerVisit(c, o.id, h.owner);
      c.events.push({
        type: "Message", player: h.owner,
        text: `key:${payStr(pay, "colour")}`,
      });
      return DONE;
    }
    case "gate": return DONE;
    case "sign": {
      c.events.push({ type: "Message", player: h.owner, text: payStr(pay, "text") });
      return DONE;
    }
    case "town": return townObject(c, heroId, o);
    case "hero":
    case "obstacle":
      return DONE;
  }
}

// -------------------------------------------------------------- economy

function takeResource(c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const r = payStr(pay, "resource", "gold") as ResourceKind;
  if (!RESOURCES.includes(r)) return DONE;
  let amount = payNum(pay, "amount", 0);
  if (amount <= 0) {
    const lo = payNum(pay, "min", 0), hi = payNum(pay, "max", 0);
    amount = hi > 0 ? rollRange(c, "map.treasure", lo, hi) : 0;
  }
  if (amount > 0) addResource(c, h.owner, r, amount);
  removeObject(c, o.id);
  return DONE;
}

function capture(c: Ctx, heroId: HeroId, o: MapObjectState): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  if (o.owner === h.owner) return DONE;
  patchObject(c, o.id, { owner: h.owner });
  c.events.push({ type: "ObjectCaptured", object: o.id, player: h.owner });
  return DONE;
}

/**
 * The town object is the *building*; the town is a `TownState` on the same
 * cell, and everything that matters - the garrison, the build list, the mage
 * guild, the siege, `captureTown` - hangs off the state and not off this.
 *
 * Which means a visit that reaches here has already been handled, or cannot
 * be. Walking onto the cell is resolved by the movement loop before the
 * occupant is ever visited: an unowned or enemy town is besieged if it is
 * defended and captured if it is not, and a town of the hero's own is entered.
 * What is left for this arm is the two ways a visit arrives with the hero
 * still standing somewhere else - a `VISIT_OBJECT` issued from an adjacent
 * cell, and a town object that no `TownState` claims - and neither may be
 * silent, which is what this branch used to be for all four cases.
 */
function townObject(c: Ctx, heroId: HeroId, o: MapObjectState): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const t = townOf(c, o);
  if (t === null) {
    // Scenery shaped like a town. A scenario may lay the object and mint no
    // town for it; saying so is the difference between a bug and a landmark.
    c.events.push({ type: "Message", player: h.owner, text: `town:unclaimed:${o.def}` });
    return DONE;
  }
  if (h.x !== t.x || h.y !== t.y || h.z !== t.z) {
    // A town is taken by walking into it, not by reaching over the wall: the
    // capture, the siege and the garrison fold all live on the movement loop
    // and none of them can run from the next cell over. `HERO_TO_TOWN` is the
    // adjacent gesture, and it is a town command rather than a visit.
    return { kind: "blocked", reason: { code: "NOT_ADJACENT" } };
  }
  // Standing in it, and the movement loop has already entered, captured or
  // besieged it above. DONE and not STOP, and the reason is the pathfinder.
  //
  // A town DOES end a walk in the classic - but only because the classic will not plot a
  // route *through* one: a town entrance is a terminus, never a through-cell.
  // `computeReachable` takes a map and movement rules and has no view of
  // `state.towns`, so every route this kernel costs walks straight over a
  // friendly hall. Returning STOP there truncates the walk in silence: on the
  // seed-77 play gate the hero was sent at a wandering monster eight cells
  // away, took three steps, halted inside his own capital with 1270 movement
  // unspent, and was told nothing. That is worse than the divergence, and it
  // is most walks out of a capital, because the capital is where heroes start.
  //
  // Make it STOP again the day `computeReachable` is given the terminal cells,
  // not before - the rule is the pathfinder's, and half of it is a defect.
  return DONE;
}

/**
 * The town this object *is*.
 *
 * Matched over the object's whole footprint rather than on its anchor: a town
 * building is several cells wide and `TownState.x/y` is its **entrance**, which
 * is almost never the anchor the sprite is pinned to. Matching the anchor alone
 * finds nothing, which is the same silence one layer down.
 */
function townOf(c: Ctx, o: MapObjectState): TownState | null {
  const footprint = c.content.mapObjects.get(o.def)?.footprint ?? [];
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.z !== o.z) continue;
    if (t.x === o.x && t.y === o.y) return t;
    for (const [dx, dy] of footprint) {
      if (t.x === o.x + dx && t.y === o.y + dy) return t;
    }
  }
  return null;
}

/**
 * External dwellings. The expansion: an owned dwelling accumulates week by week, an
 * unowned one is reset to a single week's growth (Section 13), so visiting one
 * both flags it and buys whatever has piled up.
 */
function dwelling(c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  if (o.owner !== h.owner) {
    patchObject(c, o.id, { owner: h.owner });
    c.events.push({ type: "ObjectCaptured", object: o.id, player: h.owner });
  }
  const creature = asCreatureDefId(payStr(pay, "creature"));
  const stock = payNum(pay, "stock", 0);
  const def = c.content.creatures.get(creature);
  if (def === undefined || stock <= 0) return DONE;

  const army = c.s.armies[h.army];
  if (army === undefined || !hasFreeSlotFor(army, creature)) return DONE;

  const price = def.cost;
  const pl = c.s.players[h.owner]!;
  let affordable = stock;
  for (const r of RESOURCES) {
    const per = price[r] ?? 0;
    if (per <= 0) continue;
    const canBuy = Math.floor(pl.resources[r] / per);
    if (canBuy < affordable) affordable = canBuy;
  }
  if (affordable <= 0) return DONE;
  for (const r of RESOURCES) {
    const per = price[r] ?? 0;
    if (per > 0) addResource(c, h.owner, r, -per * affordable);
  }
  addCreatures(c, h.army, creature, affordable);
  setPayload(c, o.id, { stock: stock - affordable });
  return DONE;
}

function pickUpArtifact(c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload): VisitOutcome {
  const art = asArtifactDefId(payStr(pay, "artifact"));
  if (c.content.artifacts.get(art) === undefined) { removeObject(c, o.id); return DONE; }
  giveArtifact(c, heroId, art);
  removeObject(c, o.id);
  return DONE;
}

// --------------------------------------------------------------- treasure

/**
 * A treasure chest offers gold or experience, and the classic's exchange rate is
 * fixed by the chest: 1000/1500/2000 gold against 500/1000/1500 experience.
 * Both numbers are in the payload, so a chest that offers only one of them
 * simply has the other at zero and never asks.
 */
function treasure(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const gold = payNum(pay, "gold", 0);
  const exp = payNum(pay, "experience", 0);
  if (gold > 0 && exp > 0) {
    const asked = raise(c, h.owner,
      { kind: "CHOOSE", text: "treasure", options: ["gold", "experience"] },
      optionResponses(2),
      { kind: "OBJECT", data: { sub: "TREASURE", hero: heroId, object: o.id, path: [...path] } });
    if (asked) return { kind: "pending" };
    // Another prompt is already open: take the gold rather than lose the chest.
    addResource(c, h.owner, "gold", gold);
    removeObject(c, o.id);
    return DONE;
  }
  if (gold > 0) addResource(c, h.owner, "gold", gold);
  if (exp > 0) grantExperience(c, heroId, exp);
  removeObject(c, o.id);
  return DONE;
}

export function resolveTreasureChoice(
  c: Ctx, heroId: HeroId, objectId: ObjectId, wantExperience: boolean,
): void {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined || o === undefined) return;
  const pay = payloadOf(c, o);
  if (wantExperience) grantExperience(c, heroId, payNum(pay, "experience", 0));
  else addResource(c, h.owner, "gold", payNum(pay, "gold", 0));
  c.events.push({ type: "ObjectVisited", hero: heroId, object: objectId, def: o.def });
  removeObject(c, objectId);
}

// ---------------------------------------------------------------- shrines

/**
 * A shrine teaches through `learnSpell` like every other source, so the book
 * and the Wisdom cap are checked in exactly one place. What is local to the
 * shrine is the *report*: the classic shows the hero the spell and then tells them
 * why they cannot have it, rather than refusing the visit.
 */
function shrine(c: Ctx, heroId: HeroId, pay: Payload): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const spell = asSpellDefId(payStr(pay, "spell"));
  const def = c.content.spells.get(spell);
  if (def === undefined) return DONE;
  if (payFlag(pay, "grantsBook") && !hasSpellbook(h)) {
    patchHero(c, heroId, { artifacts: { ...h.artifacts, [SPELLBOOK_SLOT]: SPELLBOOK_ARTIFACT } });
  }
  const why = learnIllegality(c, c.s.heroes[heroId]!, spell);
  if (why !== null) {
    if (why.code === "NO_SPELLBOOK") {
      c.events.push({ type: "Message", player: h.owner, text: "no_spellbook" });
    } else if (why.code === "TARGET_INVALID" && def.level > maxSpellLevel(h, c.content)) {
      c.events.push({ type: "Message", player: h.owner, text: "wisdom_required" });
    }
    return DONE;
  }
  learnSpell(c, heroId, spell);
  return DONE;
}

// --------------------------------------------------------------- scholars

function scholar(c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const amount = payNum(pay, "amount", 1);
  const primary = payStr(pay, "primary");
  if (primary !== "") {
    const key = primary as PrimarySkill;
    if (key in h.primary) {
      patchHero(c, heroId, { primary: { ...h.primary, [key]: h.primary[key] + amount } });
      removeObject(c, o.id);
      return DONE;
    }
  }
  const skill = payStr(pay, "skill");
  if (skill !== "") {
    const id = asSkillDefId(skill);
    // A new skill needs a free slot; promoting one the hero has does not.
    const room = skillLevel(h, id) > 0 || h.secondary.length < MAX_SECONDARY_SKILLS;
    if (c.content.skills.get(id) !== undefined && skillLevel(h, id) < 3 && room) {
      learnSkill(c, h, id);
      removeObject(c, o.id);
      return DONE;
    }
  }
  const spell = payStr(pay, "spell");
  // Same gate as the shrine and the mage guild; the scholar has no rule of
  // its own about who may be taught.
  if (spell !== "") learnSpell(c, heroId, asSpellDefId(spell));
  removeObject(c, o.id);
  return DONE;
}

// ----------------------------------------------------------------- bonuses

const SRC_OBJECT_MORALE = asModifierSrcId("object.morale");
const SRC_OBJECT_LUCK = asModifierSrcId("object.luck");

/**
 * The catch-all visitable: wells, temples, rally flags, learning stones,
 * arenas, gardens of revelation, obelisks. Every one of them is a set of
 * numbers in the payload, applied in a fixed order so two objects with the same
 * payload always do the same thing.
 */
function bonus(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h0 = c.s.heroes[heroId]!;
  const owner = h0.owner;

  // A counter answers for itself. Everything below is the statue case: a
  // visitable that changes a number and asks nothing.
  const served = visitService(c, heroId, o, pay, path);
  if (served !== null) return served;

  if (payFlag(pay, "obelisk") || payNum(pay, "obelisk", 0) > 0) {
    markPlayerVisit(c, o.id, owner);
    let seen = 0, total = 0;
    for (const id of entityKeys(c.s.objects)) {
      const ob = c.s.objects[id]!;
      const p = payloadOf(c, ob);
      if (!(payFlag(p, "obelisk") || payNum(p, "obelisk", 0) > 0)) continue;
      total++;
      if (p[playerMarkKey(owner)] === true) seen++;
    }
    c.events.push({ type: "Message", player: owner, text: `obelisk:${seen}/${total}` });
    return DONE;
  }

  const days = payNum(pay, "days", 1);
  const mods: Modifier[] = [];
  const morale = payNum(pay, "morale", 0);
  if (morale !== 0) {
    mods.push({
      source: SRC_OBJECT_MORALE, stat: "morale", op: "add", value: morale,
      stackGroup: "object.morale", stackRule: "highest",
      expires: { kind: "turns", turns: days },
    });
  }
  const luck = payNum(pay, "luck", 0);
  if (luck !== 0) {
    mods.push({
      source: SRC_OBJECT_LUCK, stat: "luck", op: "add", value: luck,
      stackGroup: "object.luck", stackRule: "highest",
      expires: { kind: "turns", turns: days },
    });
  }
  if (mods.length > 0) {
    patchHero(c, heroId, { modifiers: [...h0.modifiers, ...mods] });
  }

  for (const key of ["attack", "defense", "spellPower", "knowledge"] as const) {
    const d = payNum(pay, key, 0);
    if (d === 0) continue;
    const h = c.s.heroes[heroId]!;
    patchHero(c, heroId, { primary: { ...h.primary, [key]: h.primary[key] + d } });
  }

  const move = payNum(pay, "movement", 0);
  if (move !== 0) {
    const h = c.s.heroes[heroId]!;
    const left = h.movementLeft + move;
    patchHero(c, heroId, { movementLeft: left < 0 ? 0 : left });
  }

  const h1 = c.s.heroes[heroId]!;
  if (payFlag(pay, "manaFull")) {
    patchHero(c, heroId, { mana: maxMana(c, h1) });
  } else {
    const mana = payNum(pay, "mana", 0);
    if (mana !== 0) {
      // A magic well tops up but never exceeds the pool; a spring may double it,
      // which is why the cap is the payload's own `manaCap` when it sets one.
      const cap = payNum(pay, "manaCap", maxMana(c, h1));
      const v = h1.mana + mana;
      patchHero(c, heroId, { mana: v > cap ? cap : v < 0 ? 0 : v });
    }
  }

  const gold = payNum(pay, "gold", 0);
  if (gold !== 0) addResource(c, owner, "gold", gold);
  for (const r of RESOURCES) {
    if (r === "gold") continue;
    const v = payNum(pay, r, 0);
    if (v !== 0) addResource(c, owner, r, v);
  }

  const exp = payNum(pay, "experience", 0);
  if (exp > 0) grantExperience(c, heroId, exp);

  const skill = payStr(pay, "skill");
  if (skill !== "") {
    const id = asSkillDefId(skill);
    const h = c.s.heroes[heroId]!;
    if (c.content.skills.get(id) !== undefined) learnSkill(c, h, id);
  }

  if (payFlag(pay, "consumed")) removeObject(c, o.id);
  return DONE;
}

// ------------------------------------------------------------------- boats

function boat(c: Ctx, heroId: HeroId, o: MapObjectState): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  if (h.boat === o.id) {
    patchHero(c, heroId, { boat: null });
    return STOP;
  }
  // Boarding ends the day, as in the classic; the voyage starts tomorrow.
  patchHero(c, heroId, { boat: o.id, x: o.x, y: o.y, z: o.z, movementLeft: 0 });
  patchObject(c, o.id, { owner: h.owner });
  return STOP;
}

// ---------------------------------------------------------------- portals

/**
 * Monoliths and subterranean gates. Exits are every other object sharing the
 * `channel`, in id order; a two-way monolith with more than two mouths asks,
 * one with a single partner just goes. Movement ends on arrival, as in the classic.
 */
function portal(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const channel = payStr(pay, "channel");
  if (channel === "") return DONE;
  // The far end of a one-way pair is scenery from the front: a hero who walks
  // onto an exit arch has arrived somewhere, not found a door. Without this an
  // exit is an entrance back to its own entrance, which makes the pair
  // two-way and the one-way generator layout meaningless.
  if (payFlag(pay, "exitOnly")) return DONE;
  const exits = portalExits(c, o.id, channel);
  if (exits.length === 0) return STOP;
  if (exits.length === 1) { teleportTo(c, heroId, exits[0]!); return STOP; }
  const asked = raise(c, h.owner,
    { kind: "CHOOSE", text: "portal", options: exits.map((e) => e as string) },
    optionResponses(exits.length),
    { kind: "OBJECT", data: { sub: "PORTAL", hero: heroId, object: o.id, path: [...path] } });
  if (asked) return { kind: "pending" };
  teleportTo(c, heroId, exits[0]!);
  return STOP;
}

/**
 * A whirlpool takes its cut out of the weakest stack aboard.
 *
 * Weakest by total `aiValue` and not by head count, because the classic's whirlpool
 * is survivable precisely by leaving one cheap stack in the army to be eaten,
 * and counting heads would make the cheap stack the one thing it never took.
 * The last stack is never emptied: a hero with no army at all cannot walk, and
 * losing the hero to a piece of terrain is not the bargain the object offers.
 */
export function whirlpoolLoss(c: Ctx, heroId: HeroId, lossBp: number): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const army = c.s.armies[h.army];
  if (army === undefined) return;

  let worst = -1;
  let worstValue = Number.MAX_SAFE_INTEGER;
  let filled = 0;
  for (let i = 0; i < army.slots.length; i++) {
    const s = army.slots[i];
    if (s === null || s === undefined || s.count <= 0) continue;
    filled++;
    const def = c.content.creatures.get(s.def);
    const value = (def?.aiValue ?? 1) * s.count;
    if (value < worstValue) { worstValue = value; worst = i; }
  }
  if (worst < 0) return;
  const slot = army.slots[worst]!;
  const lost = Math.trunc((slot.count * lossBp) / 10000);
  const kept = slot.count - lost;
  const floor = filled === 1 ? 1 : 0;
  const left = kept < floor ? floor : kept;
  if (left === slot.count) return;
  const slots = army.slots.map((s, i) =>
    (i === worst ? (left > 0 ? { def: slot.def, count: left } : null) : s ?? null));
  c.s.armies = { ...c.s.armies, [h.army]: { ...army, slots } };
  c.events.push({
    type: "Message", player: h.owner,
    text: `whirlpool:${slot.def as string}:${String(slot.count - left)}`,
  });
}

export function portalExits(c: Ctx, from: ObjectId, channel: string): readonly ObjectId[] {
  const out: ObjectId[] = [];
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind !== "portal" || o.removed || o.id === from) continue;
    const pay = payloadOf(c, o);
    if (payStr(pay, "channel") !== channel) continue;
    if (payFlag(pay, "entryOnly")) continue;
    out.push(o.id);
  }
  return out;
}

export function teleportTo(c: Ctx, heroId: HeroId, target: ObjectId): void {
  const o = c.s.objects[target];
  if (o === undefined) return;
  const at = arrivalCell(c, o);
  patchHero(c, heroId, { x: at.x, y: at.y, z: o.z });
  // A step lights the ground it lands on; so must a jump. Nothing did, and it
  // only became visible when a player could finally reach the second level: a
  // hero came out of a subterranean gate into a plane he had never seen, with
  // no fog lifted anywhere on it - not even the cell under his own feet, whose
  // terrain his observation therefore reported as index 0, which is water. The
  // whole cavern stayed black until the turn boundary recomputed it.
  const h = c.s.heroes[heroId];
  if (h !== undefined) revealStep(c, h);

  // The whirlpool's toll is charged on ARRIVAL and read off the mouth the hero
  // comes out of, not the one they went in by. That is what makes it charge
  // once on every route into this function: `portal()` reaches here for a
  // single-exit channel and `execute.ts`'s PORTAL resume reaches here after
  // the player has picked one of several, and neither of them has the entry
  // mouth's payload in hand at that point. Both mouths of a whirlpool carry
  // `lossBp`, so which end is read does not change the answer.
  const lossBp = payNum(payloadOf(c, o), "lossBp", 0);
  if (lossBp > 0) whirlpoolLoss(c, heroId, lossBp);
}

/**
 * The cell a hero arriving through a portal stands on.
 *
 * Not the destination's anchor. A one-cell portal's anchor IS its entrance, so
 * every teleport shipped before the subterranean gate hid the difference; the
 * gate is a two-by-two gatehouse whose anchor is a blocking footprint cell and
 * whose entrance is the cell at (1, 0). A hero put on the anchor arrives inside
 * the masonry: he can still walk out, because `cellEnterable` is asked about
 * the cell being entered rather than the one being left, but he is drawn under
 * the gatehouse roof and he occupies a cell that no other hero can path to -
 * so a second hero can never follow him down onto it, and the fog he lifts is
 * centred a cell off the door he came out of.
 *
 * The entrance list is the def's own and is read in its authored order, so this
 * is deterministic; a def with no usable entrance falls back to the anchor,
 * which is what a one-cell portal wants anyway.
 */
function arrivalCell(c: Ctx, o: MapObjectState): { readonly x: number; readonly y: number } {
  const def = c.content.mapObjects.get(o.def);
  for (const [dx, dy] of def?.entrances ?? []) {
    const x = o.x + dx, y = o.y + dy;
    if (indexOf(c.s.map, x, y, o.z) >= 0) return { x, y };
  }
  return { x: o.x, y: o.y };
}

// ------------------------------------------------------------- guards

/**
 * A wandering monster. The classic asks before the fight when the stack would rather
 * join or flee than die; the offer is in the payload so the map author, not the
 * kernel, decides which stacks bargain.
 */
function monster(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const guard = guardArmyOf(c, o, pay);
  if (guard === null) { removeObject(c, o.id); return DONE; }

  // Diplomacy first, and asked EXACTLY ONCE: `diplomacyOffer` rolls on
  // `map.encounter`, so a second call is a second draw and a different answer.
  // The order is the rule's, not this file's - join, then flee, then fight -
  // and the classic prefers the offer to the fight.
  const offer = diplomacyOffer(c, h, guard, pay);

  if (offer.kind === "flee") {
    // The stack breaks rather than bargains. Nothing is fought and nothing is
    // won: the guard is gone, the ground is open, and the hero keeps walking.
    removeObject(c, o.id);
    c.events.push({ type: "Message", player: h.owner, text: "monster:fled" });
    return DONE;
  }

  if (offer.kind === "join") {
    const army = c.s.armies[guard];
    const slot = army?.slots.find((s) => s !== null && s.count > 0) ?? null;
    if (slot !== null) {
      const pl = c.s.players[h.owner];
      // A price the player cannot meet is not an offer. Say so and let the
      // ordinary fight prompt stand, rather than showing a button that is
      // refused when pressed.
      if (offer.price > 0 && (pl?.resources.gold ?? 0) < offer.price) {
        c.events.push({
          type: "Message", player: h.owner,
          text: `monster:join:poor:${String(offer.price)}`,
        });
      } else {
        setPayload(c, o.id, { joinPrice: offer.price });
        const asked = raise(c, h.owner,
          {
            kind: "CONFIRM",
            text: `monster:join:${slot.def as string}:${String(slot.count)}:${String(offer.price)}`,
          },
          OK_CANCEL,
          {
            kind: "OBJECT",
            data: { sub: "SVC_join", hero: heroId, object: o.id, path: [...path] },
          });
        // The gold is taken on the answer and never before it - the invariant
        // every counter in this file holds. A prompt that cannot be raised
        // falls through to the fight rather than recruiting unasked.
        if (asked) return { kind: "pending" };
      }
    }
  }

  const threat = armyValue(c.content, c.s.armies[guard] ?? null);
  const asked = raise(c, h.owner,
    { kind: "FIGHT_OR_FLEE", objectId: o.id, threat },
    OK_CANCEL,
    { kind: "OBJECT", data: { sub: "FIGHT_OR_FLEE", hero: heroId, object: o.id, path: [...path] } });
  if (asked) return { kind: "pending" };
  return { kind: "battle", object: o.id };
}


function bank(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const guard = guardArmyOf(c, o, pay);
  if (guard === null) { grantBankReward(c, heroId, o); return DONE; }
  const threat = armyValue(c.content, c.s.armies[guard] ?? null);
  const asked = raise(c, h.owner,
    { kind: "FIGHT_OR_FLEE", objectId: o.id, threat },
    OK_CANCEL,
    { kind: "OBJECT", data: { sub: "FIGHT_OR_FLEE", hero: heroId, object: o.id, path: [...path] } });
  if (asked) return { kind: "pending" };
  return { kind: "battle", object: o.id };
}

/**
 * A garrison is a bank with no treasure: its guard has to be beaten once,
 * and then the checkpoint belongs to whoever beat it and everybody walks
 * through. For a whole build it was `STOP` - a two-by-two blocker that ended
 * the hero's day and never fought, on every chokepoint the generator drew.
 */
function garrison(
  c: Ctx, heroId: HeroId, o: MapObjectState, pay: Payload, path: readonly number[],
): VisitOutcome {
  const h = c.s.heroes[heroId]!;
  const guard = guardArmyOf(c, o, pay);
  if (guard === null) { openGarrison(c, o.id, h.owner); return DONE; }
  const threat = armyValue(c.content, c.s.armies[guard] ?? null);
  const asked = raise(c, h.owner,
    { kind: "FIGHT_OR_FLEE", objectId: o.id, threat },
    OK_CANCEL,
    { kind: "OBJECT", data: { sub: "FIGHT_OR_FLEE", hero: heroId, object: o.id, path: [...path] } });
  if (asked) return { kind: "pending" };
  return { kind: "battle", object: o.id };
}

/** The checkpoint is taken: flagged for its taker, and its walls stop blocking. */
export function openGarrison(c: Ctx, id: ObjectId, owner: PlayerId): void {
  const o = c.s.objects[id];
  if (o === undefined) return;
  if (o.owner !== owner) patchObject(c, id, { owner });
  const footprint = c.content.mapObjects.get(o.def)?.footprint ?? [];
  if (footprint.length === 0) { setOccupant(c.s.map, o.x, o.y, o.z, id, false); return; }
  for (const [dx, dy] of footprint) setOccupant(c.s.map, o.x + dx, o.y + dy, o.z, id, false);
}

/** Materialise the guard army the first time anyone looks at the object. */
export function guardArmyOf(c: Ctx, o: MapObjectState, pay: Payload): ArmyId | null {
  if (o.guard !== null) {
    const a = c.s.armies[o.guard];
    if (a === undefined) return null;
    for (const s of a.slots) if (s !== null && s.count > 0) return o.guard;
    return null;
  }
  const creature = asCreatureDefId(payStr(pay, "creature"));
  const count = payNum(pay, "count", 0);
  if (c.content.creatures.get(creature) === undefined || count <= 0) return null;
  const id = mintArmyId(c);
  const slots = emptyArmySlots();
  slots[0] = { def: creature, count };
  putArmy(c, { id, owner: null, slots });
  patchObject(c, o.id, { guard: id });
  return id;
}

/** Everything a cleared bank or monster hoard hands over. */
/**
 * Everything a cleared bank or monster hoard hands over - once.
 *
 * The `looted` mark is the fix for a real defect: `bank()` grants whenever the
 * guard army is empty, so before this a second hero walking into an already
 * emptied Wyrm Hoard collected twenty thousand gold and twenty gems all over
 * again. `oneVisitPerHero` on the def stopped the *same* hero re-farming it and
 * nothing stopped the next one. The mark is on the object rather than on the
 * player because a bank is emptied for everybody, not per kingdom.
 *
 * `consumed` on top of that removes the object outright, which is what a
 * Mystery Casket does and what a creature bank does not: the bank is still a
 * building after you have robbed it.
 */
export function grantBankReward(c: Ctx, heroId: HeroId, o: MapObjectState): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const pay = payloadOf(c, o);
  if (payFlag(pay, "looted")) {
    c.events.push({ type: "Message", player: h.owner, text: "bank:empty" });
    return;
  }
  setPayload(c, o.id, { looted: true });
  grantReward(c, heroId, pay);
  if (payFlag(pay, "consumed")) removeObject(c, o.id);
}

// ------------------------------------------------------- weekly bookkeeping

/** Reset the objects the classic resets on a Monday, and grow the flagged dwellings. */
export function weeklyObjectReset(c: Ctx): void {
  const heroClears = new Map<HeroId, Set<string>>();
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed) continue;
    const def = c.content.mapObjects.get(o.def);
    const pay = payloadOf(c, o);

    if (o.kind === "dwelling") {
      const growth = payNum(pay, "growth", 0);
      if (growth > 0) {
        // The expansion Section 13: an owned dwelling accumulates, an unowned one is
        // clamped to a single week's worth so an untouched map does not stack
        // twenty weeks of dragons behind one gate.
        const stock = payNum(pay, "stock", 0);
        setPayload(c, id, { stock: o.owner === null ? growth : stock + growth });
      }
    }

    // The Black Market restocks on a Monday whether or not anybody bought
    // anything, which is what makes coming back next week worth the walk.
    if (payStr(pay, "service") === "blackmarket") rollBlackMarket(c, id as ObjectId);

    if (def?.weeklyReset !== true) continue;
    for (const h of o.visitedBy) {
      let set = heroClears.get(h);
      if (set === undefined) { set = new Set(); heroClears.set(h, set); }
      set.add(id as string);
    }
    patchObject(c, id, { visitedBy: [] });
  }
  for (const [hid, ids] of [...heroClears.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    const h = c.s.heroes[hid];
    if (h === undefined) continue;
    patchHero(c, hid, { visited: h.visited.filter((x) => !ids.has(x as string)) });
  }
}

/** Daily production of every flagged mine, applied at the day boundary. */
export function collectMineIncome(c: Ctx): void {
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind !== "mine" || o.removed || o.owner === null) continue;
    const pay = payloadOf(c, o);
    const r = payStr(pay, "resource", "gold") as ResourceKind;
    const amount = payNum(pay, "amount", 0);
    if (amount > 0 && RESOURCES.includes(r)) addResource(c, o.owner, r, amount);
  }
}

// ------------------------------------------------------------- garrisons

export const garrisonArmyOf = (o: MapObjectState): ArmyId | null => o.guard;


